import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import { fetchTicketComments, getProject } from "../../../services/projects";
import { isMeaningfulGigsOrDemo } from "../../../utils/auth";
import { pickCardThumbUrl } from "../components/ProjectStatusPane";
import {
  visibleAiCommentCount,
  visibleHumanCommentCount,
} from "../utils/visibleComments";

export type ChatReviewSummary = {
  id: string;
  ticketId: string;
  name: string;
  versionNumber?: number;
  thumbnailUrl?: string | null;
  aiCommentCount: number;
  humanCommentCount: number;
};

/**
 * Build review-rail card data for standalone chats from per-ticket fetches
 * plus transcript-derived titles.
 */
export function useChatReviewSummaries(
  ticketIds: readonly string[],
  titleMap: Map<string, string>,
): { reviews: ChatReviewSummary[]; isLoading: boolean } {
  const isDemoEnterprise = isMeaningfulGigsOrDemo();

  const ticketQueries = useQueries({
    queries: ticketIds.map((ticketId) => ({
      queryKey: ["ticket-view", ticketId],
      queryFn: () => getProject(ticketId),
      staleTime: 60_000,
    })),
  });

  const commentQueries = useQueries({
    queries: ticketIds.map((ticketId) => ({
      queryKey: ["ticket-view-comments", ticketId],
      queryFn: () => fetchTicketComments({ ticketId, query: "" }),
      staleTime: 60_000,
    })),
  });

  const isLoading =
    ticketQueries.some((q) => q.isLoading) ||
    commentQueries.some((q) => q.isLoading);

  const reviews = useMemo((): ChatReviewSummary[] => {
    return ticketIds.map((ticketId, index) => {
      const ticket = ticketQueries[index]?.data;
      const comments = commentQueries[index]?.data ?? [];
      const boards = ticket?.revisionBoards?.filter((b) => !b.deletedAt) ?? [];
      const latestBoard = boards.at(-1);
      const firstReviewFile = latestBoard?.reviewFiles?.[0] as
        | { source?: string; thumbnailUrl?: string }
        | undefined;
      const thumbnailUrl = pickCardThumbUrl(
        firstReviewFile,
        latestBoard,
        isDemoEnterprise,
      );
      const aiCommentCount = visibleAiCommentCount(comments);
      const humanCommentCount = visibleHumanCommentCount(comments);
      const name =
        titleMap.get(ticketId) ?? ticket?.title ?? `Review ${index + 1}`;
      const versionNumber = boards.length > 0 ? boards.length : undefined;

      return {
        id: ticketId,
        ticketId,
        name,
        versionNumber,
        thumbnailUrl: thumbnailUrl ?? null,
        aiCommentCount,
        humanCommentCount,
      };
    });
  }, [ticketIds, ticketQueries, commentQueries, titleMap, isDemoEnterprise]);

  return { reviews, isLoading };
}
